<template>
  <div
    class="grid grid-flow-col auto-cols-[minmax(250px,1fr)] items-start gap-3 overflow-x-auto pb-1.5"
    :data-testid="testId"
  >
    <section
      v-for="column in resolvedColumns"
      :key="column.key"
      class="flex flex-col gap-2 rounded-lg border-t-[3px] bg-lightGray30/40 p-2.5"
      :class="QUEUE_TONE_BORDER_CLASS[column.tone ?? 'neutral']"
      :data-testid="`${testId}-column-${column.key}`"
    >
      <h3
        class="m-0 flex items-center justify-between gap-2 text-[13px] font-bold text-textPrimary"
      >
        <slot name="column-title" :column="column">{{ column.title }}</slot>
        <span class="font-medium tabular-nums text-neutral50">{{ headerCount(column) }}</span>
      </h3>
      <p
        v-if="column.hint"
        class="m-0 text-[11px] text-neutral50"
        :data-testid="`${testId}-column-${column.key}-hint`"
      >
        {{ column.hint }}
      </p>
      <template v-if="column.rows.length">
        <!--
          O slot expõe só a linha. Como o conteúdo do slot é compilado no escopo
          do pai, quem usa o Kanban liga os eventos do cartão (ex.: `@action`)
          direto no próprio template, sem o Kanban repassar nada.
        -->
        <template v-for="row in column.rows" :key="rowKey(row)">
          <slot name="card" :row="row" />
        </template>
      </template>
      <p v-else-if="!columnHasMore?.[column.key]" class="m-0 text-xs text-neutral50">
        {{ emptyColumnText }}
      </p>
      <!--
        Carga por coluna (opcional): só aparece quando o pai informa que há mais
        linhas daquele grupo no servidor. Sem as props, nada muda.
      -->
      <button
        v-if="columnHasMore?.[column.key]"
        type="button"
        class="rounded border border-lightGray30 bg-white px-2.5 py-1.5 text-xs font-medium text-primary hover:border-primary disabled:cursor-wait disabled:opacity-60"
        :disabled="isColumnLoading(column.key)"
        :aria-busy="isColumnLoading(column.key) ? 'true' : 'false'"
        :aria-label="loadMoreLabel(column)"
        :data-testid="`${testId}-column-${column.key}-load-more`"
        @click="onLoadMore(column.key)"
      >
        {{ isColumnLoading(column.key) ? 'Carregando…' : 'Ver mais' }}
      </button>
    </section>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed } from 'vue'

import { QUEUE_TONE_BORDER_CLASS } from './types'
import type { QueueKanbanColumn } from './types'

const props = withDefaults(
  defineProps<{
    rows: T[]
    columns: QueueKanbanColumn<T>[]
    rowKey: (row: T) => number
    emptyColumnText?: string
    testId?: string
    /**
     * Total REAL de cada coluna (ex.: contadores do servidor), quando as linhas
     * carregadas são só uma parte. Coluna ausente mostra a contagem carregada.
     */
    columnTotals?: Partial<Record<string, number>>
    /** Colunas com mais linhas no servidor: mostram o botão "Ver mais". */
    columnHasMore?: Partial<Record<string, boolean>>
    /** Colunas buscando a próxima página: o botão fica desabilitado. */
    columnLoading?: Partial<Record<string, boolean>>
  }>(),
  {
    emptyColumnText: 'Nada aqui.',
    testId: 'queue-kanban',
    columnTotals: undefined,
    columnHasMore: undefined,
    columnLoading: undefined
  }
)

const emit = defineEmits<{
  /** Pedido da próxima página de UMA coluna; quem busca é o pai. */
  'load-more': [columnKey: string]
}>()

defineSlots<{
  card(props: { row: T }): unknown
  'column-title'(props: { column: QueueKanbanColumn<T> & { rows: T[] } }): unknown
}>()

/**
 * Cada linha entra na PRIMEIRA coluna cujo predicado casa. Uma passada só,
 * em vez de um `filter` por coluna: assim um predicado mal escrito duplica
 * nada — e o que não casa em coluna nenhuma fica evidente para quem define as
 * colunas (é o pai que garante a cobertura total).
 */
type ResolvedColumn = QueueKanbanColumn<T> & { rows: T[] }

const headerCount = (column: ResolvedColumn): number =>
  props.columnTotals?.[column.key] ?? column.rows.length

const isColumnLoading = (key: string): boolean => props.columnLoading?.[key] === true

const loadMoreLabel = (column: ResolvedColumn): string =>
  `Ver mais em ${column.title} (${column.rows.length} de ${headerCount(column)})`

function onLoadMore(key: string): void {
  if (isColumnLoading(key)) return
  emit('load-more', key)
}

const resolvedColumns = computed(() => {
  const buckets: ResolvedColumn[] = props.columns.map((column) => ({ ...column, rows: [] as T[] }))
  for (const row of props.rows) {
    const bucket = buckets.find((candidate) => candidate.matches(row))
    if (bucket) bucket.rows.push(row)
  }
  return buckets
})
</script>
